import React from "react";
import { Routes, Route, NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Shield, LogOut, LayoutDashboard, Building2, CreditCard, LifeBuoy, BarChart3, ScrollText } from "lucide-react";
import AdminDashboard from "./AdminDashboard";
import AdminOrganizations from "./AdminOrganizations";
import AdminOrgDetail from "./AdminOrgDetail";
import AdminBilling from "./AdminBilling";
import AdminSupport from "./AdminSupport";
import AdminAnalytics from "./AdminAnalytics";
import AdminAuditLogs from "./AdminAuditLogs";

const TABS = [
  { to: "/admin", label: "Vue d'ensemble", icon: LayoutDashboard, end: true, testId: "admin-nav-dashboard" },
  { to: "/admin/organisations", label: "Organisations", icon: Building2, testId: "admin-nav-organizations" },
  { to: "/admin/abonnements", label: "Abonnements", icon: CreditCard, testId: "admin-nav-billing" },
  { to: "/admin/support", label: "Support", icon: LifeBuoy, testId: "admin-nav-support", badge: true },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testId: "admin-nav-analytics" },
  { to: "/admin/logs", label: "Logs admin", icon: ScrollText, testId: "admin-nav-logs" },
];

export default function AdminCockpit() {
  const { user, logout } = useAuth();
  const { data: attention } = useQuery({
    queryKey: ["admin-support-attention"],
    queryFn: async () => (await api.get("/api/admin/support/attention")).data,
    retry: false, refetchInterval: 60000,
  });
  const badge = attention?.attention || 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-cockpit">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-white"><Shield className="h-5 w-5" /></div>
          <div>
            <p className="text-sm font-bold text-slate-900">ActiPilote · Back-office</p>
            <p className="text-xs text-slate-500">{user?.email}</p>
          </div>
        </div>
        <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"><LogOut className="h-4 w-4" /> Déconnexion</button>
      </header>

      <nav className="overflow-x-auto border-b border-slate-200 bg-white px-6">
        <div className="mx-auto flex max-w-[1200px] gap-1">
          {TABS.map((t) => (
            <NavLink key={t.to} to={t.to} end={t.end} data-testid={t.testId}
              className={({ isActive }) => `flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${isActive ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
              {t.badge && badge > 0 && <span data-testid="admin-support-badge" className="ml-0.5 rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{badge}</span>}
            </NavLink>
          ))}
        </div>
      </nav>

      <main className="mx-auto max-w-[1200px] p-6">
        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="organisations" element={<AdminOrganizations />} />
          <Route path="organisations/:oid" element={<AdminOrgDetail />} />
          <Route path="abonnements" element={<AdminBilling />} />
          <Route path="support" element={<AdminSupport />} />
          <Route path="support/:ticketId" element={<AdminSupport />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="logs" element={<AdminAuditLogs />} />
        </Routes>
      </main>
    </div>
  );
}
